import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '@/api/subscriptions';
import { DARK_THEME } from '@/stores/themeStore';

interface CollabUpgradeGateProps {
  trialUsed: boolean;
  onTrialStarted: () => void;
  onChangeTeam?: () => void;
}

const COLLAB_FEATURES = [
  { icon: 'ri-live-line',       label: 'Sessões ao vivo com sua equipe' },
  { icon: 'ri-scroll-to-top-line', label: 'Sincronização de rolagem em tempo real' },
  { icon: 'ri-music-2-line',    label: 'Seleção de músicas pelo líder' },
  { icon: 'ri-notification-3-line', label: 'Avisos ao vivo (mudança, pausa, rallentando...)' },
  { icon: 'ri-team-line',       label: 'Múltiplos membros simultâneos' },
];

export const CollabUpgradeGate = ({ trialUsed, onTrialStarted, onChangeTeam }: CollabUpgradeGateProps) => {
  const navigate = useNavigate();
  const theme = DARK_THEME;
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const handleStartTrial = async () => {
    setStarting(true);
    setError('');
    try {
      await subscriptionApi.startTrial();
      onTrialStarted();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? 'Não foi possível iniciar o trial. Tente novamente.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div
      className="flex-1 flex items-center justify-center px-6 py-12"
      style={{ backgroundColor: theme.colors.bg.primary }}
    >
      <div className="w-full max-w-lg mx-auto text-center">

        {/* Ícone */}
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ backgroundColor: `${theme.colors.accent}15` }}
        >
          <i className="ri-live-line text-4xl" style={{ color: theme.colors.accent }}></i>
        </div>

        {/* Título */}
        <h2
          className="text-3xl font-semibold mb-3"
          style={{ color: theme.colors.text.primary, fontFamily: "'Playfair Display', serif" }}
        >
          Colaboração ao Vivo
        </h2>
        <p className="text-base mb-8" style={{ color: theme.colors.text.tertiary }}>
          Esse recurso está disponível nos planos <strong style={{ color: theme.colors.text.secondary }}>Group</strong> e <strong style={{ color: theme.colors.text.secondary }}>Unlimited</strong>.
          <br />
          Todos na equipe enxergam a mesma música, no mesmo tom, ao mesmo tempo.
        </p>

        {/* Funcionalidades */}
        <div
          className="rounded-2xl p-6 mb-8 text-left"
          style={{ backgroundColor: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border}` }}
        >
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: theme.colors.text.tertiary }}>
            O que você vai poder fazer
          </p>
          <ul className="flex flex-col gap-3">
            {COLLAB_FEATURES.map((f) => (
              <li key={f.label} className="flex items-center gap-3">
                <i className={`${f.icon} text-lg flex-shrink-0`} style={{ color: theme.colors.accent }}></i>
                <span className="text-sm" style={{ color: theme.colors.text.secondary }}>{f.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Erro */}
        {error && (
          <p className="text-sm text-red-400 mb-4">{error}</p>
        )}

        {/* CTAs */}
        <div className="flex flex-col gap-3">
          {!trialUsed && (
            <button
              onClick={handleStartTrial}
              disabled={starting}
              className="w-full py-4 rounded-xl font-semibold text-base transition-all disabled:opacity-60 cursor-pointer"
              style={{
                backgroundColor: theme.colors.accent,
                color: theme.colors.bg.primary,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.colors.accentHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = theme.colors.accent; }}
            >
              {starting ? 'Iniciando...' : 'Experimentar grátis por 7 dias'}
            </button>
          )}

          <button
            onClick={() => navigate('/checkout?plan=group')}
            className="w-full py-4 rounded-xl font-semibold text-base transition-all cursor-pointer border"
            style={{
              backgroundColor: 'transparent',
              color: theme.colors.accent,
              borderColor: `${theme.colors.accent}50`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.colors.accent}10`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Ver planos e assinar
          </button>
          {onChangeTeam && (
            <button
              onClick={onChangeTeam}
              className="w-full py-3 font-medium text-sm transition-opacity hover:opacity-70 cursor-pointer"
              style={{ color: theme.colors.text.tertiary }}
            >
              Trocar equipe
            </button>
          )}
        </div>

        {trialUsed && (
          <p className="text-xs mt-4" style={{ color: theme.colors.text.tertiary }}>
            Você já utilizou o período de trial gratuito.
          </p>
        )}
      </div>
    </div>
  );
};
